import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";
import * as pdfjsLib from "pdfjs-dist";

interface PdfViewerState {
  page: number;
  scale: number;
  totalPages: number;
  loading: boolean;
  open: boolean;
  blobUrl: string | null;

  $refs: {
    canvas: HTMLCanvasElement;
    pdfContainer: HTMLDivElement;
  };

  onDoubleTap: () => void;
  nextPage: () => void;
  prevPage: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetScale: () => void;
  openViewer: () => void;
  renderPage: () => Promise<void>;
}

function onDoubleTap(this: PdfViewerState) {
  this.scale = this.scale === 1 ? 2 : 1;
  this.renderPage();
}

async function nextPage(this: PdfViewerState) {
  if (this.page < this.totalPages) {
    this.page++;
    await this.renderPage();
  }
}

async function prevPage(this: PdfViewerState) {
  if (this.page > 1) {
    this.page--;
    await this.renderPage();
  }
}

async function zoomIn(this: PdfViewerState) {
  this.scale = this.scale + 0.25;
  await this.renderPage();
}

async function zoomOut(this: PdfViewerState) {
  if (this.scale > 1) {
    this.scale = this.scale - 0.25;
    await this.renderPage();
  }
}

async function resetScale(this: PdfViewerState) {
  if (this.scale !== 1) {
    this.scale = 1;
    await this.renderPage();
  }
}

function alpineData() {
  let pdfDoc: PDFDocumentProxy | null = null;
  let renderTask: RenderTask | null = null;

  return {
    page: 1,
    scale: 1,
    totalPages: 0,
    loading: true,
    open: false,
    blobUrl: null,
    onDoubleTap,
    nextPage,
    prevPage,
    zoomIn,
    zoomOut,
    resetScale,

    async openViewer(this: PdfViewerState, url: string) {
      this.open = true;

      if (pdfDoc) return;

      this.loading = true;

      const response = await fetch(url);
      const blob = await response.blob();
      this.blobUrl = URL.createObjectURL(blob);

      pdfjsLib.GlobalWorkerOptions.workerSrc = "/assets/pdf.worker.min.js";

      pdfDoc = await pdfjsLib.getDocument(this.blobUrl).promise;
      this.totalPages = pdfDoc.numPages;
      document.getElementById("pdf-viewer-wrapper")?.classList.remove("hidden");

      await this.renderPage();

      this.loading = false;
    },

    async renderPage(this: PdfViewerState) {
      if (!pdfDoc) return;
      this.loading = true;

      const page = await pdfDoc.getPage(this.page);
      const container = this.$refs.pdfContainer.clientWidth;
      const baseViewport = page.getViewport({ scale: 1 });
      const fitScale = container / baseViewport.width;
      const scale = fitScale * this.scale;
      const viewport = page.getViewport({ scale });
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
      const outputScale = window.devicePixelRatio || 1;

      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);

      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;

      const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined;

      renderTask?.cancel();

      renderTask = page.render({
        canvasContext: ctx,
        viewport,
        transform,
      });

      await renderTask.promise;
      this.loading = false;
    },

    close(this: PdfViewerState) {
      this.open = false;
      this.scale = 1;
      document.getElementById("pdf-viewer-wrapper")?.classList.add("hidden");
      pdfDoc = null;
      renderTask = null;
    },
  };
}

export default alpineData;
